import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { sanitizeDocIdSync, validateFirestorePath } from '../utils/pmt2PathUtils';

// Base collection name
const LEDGER_COLLECTION_BASE = 'pmt2_ledger';

// Interface for individual history entries
export interface FarmingBidHistoryEntry {
  id: string;
  date: Timestamp; // Firestore Timestamp
  Date: string; // String date (e.g., "MM/DD/YY")
  'Your Name': string;
  'New Value': string;
  note: string;
  // Allow for other potential fields from the ledger row
  [key: string]: any;
}

// Interface for the data structure returned by the service
export interface FarmingBidHistoryData {
  [simplifiedTitle: string]: FarmingBidHistoryEntry[];
}

// Define the mapping between Data Element names and simplified titles
const BID_TYPE_MAPPING: { [key: string]: string } = {
  'Farming Override Bid': 'Farming',
  'Broad Override Bid': 'Broad',
  'CTA Override Bid': 'CTA',
  'Full Auto Override Bid': 'Full Auto',
  'Full Auto Close-Match Bid Override': 'Full Auto Close-Match',
  'Full Auto Loose-Match Bid Override': 'Full Auto Loose-Match',
  'Full Auto Substitutes Bid Override': 'Full Auto Substitutes',
  'Full Auto Complements Bid Override': 'Full Auto Complements',
};

const DATA_ELEMENT_NAMES = Object.keys(BID_TYPE_MAPPING);

/**
 * Fetches farming bid history data for multiple bid types for a given ASIN.
 *
 * @param companyName The company name.
 * @param asin The ASIN to fetch data for.
 * @returns An object mapping simplified bid type titles to their history entries.
 */
export const getFarmingBidHistoryData = async (
  companyName: string,
  asin: string
): Promise<FarmingBidHistoryData> => {
  const sanitizedAsin = sanitizeDocIdSync(asin, {
    maxLength: 100,
    context: 'farming-bid-asin'
  });
  const historyData: FarmingBidHistoryData = {};

  console.log(`[FarmingBidHistoryService] Fetching data for ${companyName}/${sanitizedAsin}`);

  try {
    const fetchPromises = DATA_ELEMENT_NAMES.map(async (dataElement) => {
      const sanitizedDataElement = sanitizeDocIdSync(dataElement, {
        maxLength: 100,
        context: 'farming-bid-dataElement'
      });
      
      // Construct and validate the full path
      const fullPath = `${LEDGER_COLLECTION_BASE}/${companyName}/${sanitizedAsin}/${sanitizedDataElement}`;
      const pathValidation = validateFirestorePath(fullPath);
      
      if (!pathValidation.valid) {
        console.warn(`[FarmingBidHistoryService] Path validation failed for ${dataElement}: ${pathValidation.message}`);
      }
      
      const docRef = doc(
        db,
        LEDGER_COLLECTION_BASE,
        companyName,
        sanitizedAsin,
        sanitizedDataElement
      );

      try {
        const docSnap = await getDoc(docRef);
        const simplifiedTitle = BID_TYPE_MAPPING[dataElement];

        if (docSnap.exists()) {
          const data = docSnap.data();
          // Ensure entries is an array and handle potential undefined/null cases
          const entries = (data?.entries || []) as FarmingBidHistoryEntry[];
          if (entries.length > 0) {
             console.log(`[FarmingBidHistoryService] Found ${entries.length} entries for ${simplifiedTitle}`);
             historyData[simplifiedTitle] = entries;
          } else {
             console.log(`[FarmingBidHistoryService] No entries found for ${simplifiedTitle}`);
             historyData[simplifiedTitle] = []; // Ensure key exists even if empty
          }
        } else {
          console.log(`[FarmingBidHistoryService] Document not found for ${simplifiedTitle}`);
          historyData[simplifiedTitle] = []; // Ensure key exists even if doc not found
        }
      } catch (error) {
         console.error(`[FarmingBidHistoryService] Error fetching document for ${dataElement}:`, error);
         // Assign empty array on error for this specific type
         historyData[BID_TYPE_MAPPING[dataElement]] = [];
      }
    });

    // Wait for all fetches to complete
    await Promise.all(fetchPromises);

    console.log(`[FarmingBidHistoryService] Finished fetching all bid types. Found data for: ${Object.keys(historyData).filter(k => historyData[k].length > 0).join(', ')}`);

    return historyData;

  } catch (error) {
    console.error(`[FarmingBidHistoryService] General error fetching farming bid history for ${companyName}/${asin}:`, error);
    // In case of a general error (e.g., Promise.all rejection, though unlikely with individual catches), return an empty object or rethrow
    // Returning empty object might be safer for the UI
    return {};
    // Or rethrow: throw error;
  }
};